import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-background border text-foreground  [&_[data-slot=alert-description]]:text-muted-foreground",
        info: "bg-info/10 border text-info border-info dark:border-info-800 dark:bg-info-950 dark:text-info-300 [&>svg]:text-current [&_[data-slot=alert-description]]:text-info/90 dark:[&_[data-slot=alert-description]]:text-info-300/90",
        warning:
          "bg-warning-50 border border-warning text-warning-800 dark:border-warning-800 dark:bg-warning-950 dark:text-warning-300 [&>svg]:text-current [&_[data-slot=alert-description]]:text-warning-800/90 dark:[&_[data-slot=alert-description]]:text-warning-300/90",
        destructive:
          "text-destructive border border-destructive bg-card dark:text-destructive-300 dark:bg-destructive-950 [&>svg]:text-current [&_[data-slot=alert-description]]:text-destructive/90 dark:[&_[data-slot=alert-description]]:text-destructive-300/90",
        "default-solid":
          "bg-card border-border border-t-4 text-foreground [&_[data-slot=alert-description]]:text-foreground",
        "info-solid":
          "bg-card border-border border-t-info border-t-4 text-foreground [&>svg]:text-current [&_[data-slot=alert-description]]:text-foreground",
        "warning-solid":
          "bg-card border-border border-t-warning border-t-4 text-foreground [&>svg]:text-current [&_[data-slot=alert-description]]:text-foreground",
        "destructive-solid":
          "bg-card border-border border-t-destructive border-t-4 text-foreground [&>svg]:text-current [&_[data-slot=alert-description]]:text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  solid?: boolean;
}

function Alert({ className, variant, solid, ...props }: AlertProps) {
  if (solid) {
    const solidVariant = variant ? `${variant}-solid` : ("default-solid" as any);
    return <div className={cn(alertVariants({ variant: solidVariant }), className)} {...props} />;
  }
  return <div data-slot="alert" role="alert" className={cn(alertVariants({ variant }), className)} {...props} />;
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn("col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight", className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn("col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed", className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
